"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ShieldCheck,
  Store,
  Truck,
} from "lucide-react";
import { Button } from "@/components/UI/Button";
import { pushEcommerceEvent } from "@/lib/analytics/gtm";
import { useCart } from "@/hooks/useCart";
import {
  canAttributeOptionMatch,
  resolveSelectedVariation,
} from "@/lib/commerce/variation";
import { getProductPaymentInfo } from "@/lib/commerce/productPayment";
import type { ProductBrand } from "@/types/brand";
import type { BoughtTogetherItem } from "@/types/boughtTogether";
import type { ProductFamilyResponse } from "@/types/productFamily";
import type { Product } from "@/types/product";
import { getProductHref, SITE_URL } from "@/lib/routing/storefrontUrls";
import { BackInStockForm } from "./BackInStockForm";
import { FrequentlyBoughtTogether } from "./FrequentlyBoughtTogether";
import { ProductFamilyOptions } from "./ProductFamilyOptions";
import { ProductPaymentMethods } from "./ProductPaymentMethods";
import { ProductQuantity } from "./ProductQuantity";
import { ShippingCalculator } from "@/components/Shipping/ShippingCalculator";
import { ProductFreeShippingNotice } from "./ProductFreeShippingNotice";
import { ProductCartFreeShipping } from "./ProductCartFreeShipping";
import type { FirstPurchasePromo } from "@/lib/coupons/couponPromo";
import { getProductShippingContextKey } from "@/lib/commerce/shippingCalculator";
import type { SelectedShippingRate } from "@/types/shipping";

interface ProductPurchasePanelProps {
  product: Product;
  brand?: ProductBrand;
  boughtTogetherItems?: BoughtTogetherItem[];
  productFamily?: ProductFamilyResponse;
  stockNotificationEnabled: boolean;
  /** "Avise-me" nativo: mostra o campo de WhatsApp opcional. */
  stockNotificationWhatsapp?: boolean;
  /** Cupom de primeira compra ativo (cadastrado no painel), para o selo. */
  firstPurchasePromo?: FirstPurchasePromo | null;
}

// Hash curto (djb2) do carrinho para a chave da cotação de frete guardada.
function shortHash(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) hash = ((hash << 5) + hash + value.charCodeAt(index)) | 0;
  return (hash >>> 0).toString(36);
}

// Página da marca: o permalink quando vier (WooCommerce); no catálogo nativo
// a marca não traz permalink e o logo sumia (2026-10-19).
function brandHref(brand: ProductBrand): string {
  return brand.permalink || `/marca/${encodeURIComponent(brand.slug)}`;
}

export function ProductPurchasePanel({
  product,
  brand,
  boughtTogetherItems = [],
  productFamily,
  stockNotificationEnabled,
  stockNotificationWhatsapp = false,
  firstPurchasePromo = null,
}: ProductPurchasePanelProps) {
  const mainPurchaseButtonRef = useRef<HTMLButtonElement>(null);
  const [quantity, setQuantity] = useState(1);
  const [purchaseMessage, setPurchaseMessage] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [selectedShipping, setSelectedShipping] =
    useState<SelectedShippingRate>();
  const [showStickyPurchase, setShowStickyPurchase] = useState(false);
  const {
    addItem,
    cart,
    calculateShippingPostcode,
    selectShippingRate,
  } = useCart();
  const variationAttributes = useMemo(
    () => product.attributes.filter((attribute) => attribute.hasVariations),
    [product.attributes],
  );
  const selectedVariation = useMemo(
    () => resolveSelectedVariation(product.variations, selectedAttributes),
    [product.variations, selectedAttributes],
  );
  const isVariable = product.type === "variable";
  const isSupportedType =
    product.type === undefined ||
    product.type === "simple" ||
    product.type === "variable";
  const variationSelectionMessage =
    isVariable && !selectedVariation
      ? "Selecione todas as opções do produto."
      : selectedVariation && !selectedVariation.inStock
        ? "Produto fora de estoque."
        : selectedVariation && !selectedVariation.purchasable
          ? "Esta combinação não está disponível."
          : "";
  const activePrice = selectedVariation?.price ?? product.price;
  const regularPrice =
    selectedVariation?.regularPrice ?? product.regularPrice;
  const hasDiscount =
    regularPrice !== undefined && regularPrice > activePrice;
  const payment = getProductPaymentInfo({
    currentPrice: activePrice,
    isVariable: isVariable && !selectedVariation,
  });
  const discountPercentage =
    hasDiscount && regularPrice
      ? Math.round((1 - payment.pixPrice / regularPrice) * 100)
      : 0;
  const currencyFormatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: product.currencyCode,
  });
  const shippingProduct = useMemo(
    () => ({
      productId: product.id,
      variationId: selectedVariation?.id,
      quantity,
      variation: selectedVariation?.attributes.map((attribute) => ({
        attribute: attribute.name,
        value: attribute.value,
      })),
    }),
    [product.id, quantity, selectedVariation],
  );
  // O frete do produto considera o carrinho (frete grátis do pedido): o
  // carrinho entra na chave para a cotação guardada não ficar velha.
  const cartSignature = (cart?.items ?? []).map((item) => `${item.key}:${item.quantity}`).join("|");
  const shippingContextKey = `${getProductShippingContextKey(shippingProduct)}${cartSignature ? `:c${shortHash(cartSignature)}` : ""}`;

  useEffect(() => {
    const purchaseButton = mainPurchaseButtonRef.current;
    if (!purchaseButton || !product.available) return;

    const observer = new IntersectionObserver(([entry]) => {
      const hasPassedAboveViewport =
        !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      setShowStickyPurchase(hasPassedAboveViewport);
    });

    observer.observe(purchaseButton);
    return () => observer.disconnect();
  }, [product.available]);

  useEffect(() => {
    pushEcommerceEvent("view_item", {
      currency: product.currencyCode,
      value: product.price,
      free_shipping: product.freeShipping === true,
      items: [
        {
          item_id: product.sku || String(product.id),
          // Código do catálogo da Meta (g:id do feed) para o Pixel.
          content_id: String(product.id),
          item_name: product.name,
          item_brand: brand?.name ?? product.brand,
          item_category: product.categories[0]?.name,
          price: product.price,
          free_shipping: product.freeShipping === true,
          quantity: 1,
        },
      ],
    });
    // Deve disparar uma única vez por produto visitado — não a cada troca de
    // variação selecionada (isso seria select_item/select_content, não
    // view_item).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  async function handlePurchase() {
    if (!isSupportedType) return;

    if (isVariable && !selectedVariation) {
      setPurchaseMessage("Selecione todas as opções do produto.");
      return;
    }

    if (
      selectedVariation &&
      (!selectedVariation.purchasable || !selectedVariation.inStock)
    ) {
      setPurchaseMessage("Esta combinação não está disponível.");
      return;
    }

    setIsAdding(true);
    setPurchaseMessage("");
    const result = await addItem(
      selectedVariation
        ? {
            productId: product.id,
            variationId: selectedVariation.id,
            quantity,
            variation: selectedVariation.attributes.map((attribute) => ({
              attribute: attribute.name,
              value: attribute.value,
            })),
          }
        : { productId: product.id, quantity },
    );

    if (result.success && selectedShipping) {
      const shippingResult = await calculateShippingPostcode(
        selectedShipping.postcode,
      );
      const matchingPackage = shippingResult.cart?.shippingPackages.find(
        (shippingPackage) =>
          shippingPackage.rates.some(
            (rate) => rate.rateId === selectedShipping.rateId,
          ),
      );

      if (matchingPackage) {
        await selectShippingRate(
          matchingPackage.packageId,
          selectedShipping.rateId,
        );
      }
    }

    setPurchaseMessage(result.message);
    setIsAdding(false);
  }

  return (
    <div className="min-w-0 max-w-full">
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:justify-between">
        <h1 className="min-w-0 flex-1 text-2xl font-bold leading-tight text-heading sm:text-3xl">
          {product.name}
        </h1>
        {brand?.image ? (
          <Link
            href={brandHref(brand)}
            prefetch={false}
            className="flex h-12 w-20 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-2 transition-colors hover:border-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:h-16 sm:w-28"
            aria-label={`Conhecer a marca ${brand.name}`}
            title={`Conhecer a marca ${brand.name}`}
          >
            <Image
              src={brand.image.src}
              alt={brand.image.alt || `Logo ${brand.name}`}
              width={112}
              height={64}
              sizes="(min-width: 640px) 112px, 96px"
              className="h-auto max-h-full w-auto max-w-full object-contain"
            />
          </Link>
        ) : null}
      </div>
      <p className="mt-3 text-sm text-muted">
        {brand ? (
          <>
            Marca:{" "}
            <Link href={brandHref(brand)} prefetch={false} className="font-medium text-heading underline-offset-2 hover:underline">
              {brand.name}
            </Link>
            <span aria-hidden="true" className="mx-2 text-slate-300">|</span>
          </>
        ) : null}
        SKU: {product.sku}
      </p>

      <div className="mt-6 border-y border-slate-200 py-5">
        {hasDiscount ? (
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted line-through">
              {currencyFormatter.format(regularPrice)}
            </p>
            {discountPercentage > 0 ? (
              <span className="rounded-md bg-emerald-700 px-2 py-1 text-xs font-bold text-white">
                -{discountPercentage}%
              </span>
            ) : null}
          </div>
        ) : null}
        <p className="mt-1 text-3xl font-bold text-emerald-700">
          {currencyFormatter.format(payment.pixPrice)}
          <span className="ml-2 text-sm font-semibold">no Pix</span>
        </p>
        {payment.installments === 1 ? (
          <p className="mt-2 text-sm font-medium leading-5 text-muted">
            ou {currencyFormatter.format(payment.currentPrice)} sem juros no
            cartão
          </p>
        ) : (
          <p className="mt-2 text-sm font-medium leading-5 text-muted">
            ou {currencyFormatter.format(payment.currentPrice)} em até{" "}
            {payment.installments}x de{" "}
            {currencyFormatter.format(payment.installmentValue)} sem juros no
            cartão
          </p>
        )}
        {firstPurchasePromo && product.available ? (
          <p className="mt-3 inline-flex flex-wrap items-center gap-x-1.5 rounded-xl border border-dashed border-secondary bg-orange-50 px-3 py-2 text-sm text-slate-800">
            <span aria-hidden="true">🎟️</span>
            <strong className="text-secondary">Primeira compra {firstPurchasePromo.label}</strong>
            <span>– Cupom: <strong className="font-mono tracking-wide">{firstPurchasePromo.code}</strong></span>
          </p>
        ) : null}
        {product.freeShipping ? <ProductFreeShippingNotice contextKey={shippingContextKey} /> : null}
        <ProductPaymentMethods
          payment={payment}
          currencyCode={product.currencyCode}
        />
        {product.available ? (
          <ProductCartFreeShipping storefrontId={selectedVariation?.id ?? product.id} quantity={quantity} />
        ) : null}
      </div>

      <div className="mt-5">
        <p
          className={`text-sm font-semibold ${
            product.available ? "text-emerald-700" : "text-red-700"
          }`}
        >
          {product.available
            ? product.stockQuantity !== undefined
              ? product.stockQuantity === 1
                ? "Disponível — última unidade em estoque"
                : `Disponível — últimas ${product.stockQuantity} unidades em estoque`
              : "Disponível"
            : "Em falta — consulte a disponibilidade"}
        </p>
        <p className="mt-3 leading-7 text-foreground">
          {product.shortDescription}
        </p>
      </div>

      {isVariable
        ? variationAttributes.map((attribute) => {
            const attributeName = attribute.taxonomy || attribute.name;

            return (
              <div key={attributeName} className="mt-5">
                <label
                  htmlFor={`variation-${attribute.id}`}
                  className="mb-2 block text-sm font-medium text-foreground"
                >
                  {attribute.name} <span aria-hidden="true">*</span>
                </label>
                <select
                  id={`variation-${attribute.id}`}
                  required
                  value={selectedAttributes[attributeName] ?? ""}
                  onChange={(event) => {
                    setSelectedAttributes((current) => ({
                      ...current,
                      [attributeName]: event.target.value,
                    }));
                    setPurchaseMessage("");
                  }}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">
                    Selecionar {attribute.name.toLocaleLowerCase("pt-BR")}
                  </option>
                  {attribute.options.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={
                        !canAttributeOptionMatch(
                          product.variations,
                          selectedAttributes,
                          attributeName,
                          option.value,
                        )
                      }
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })
        : null}

      <div className="mt-6">
        {!isSupportedType ? (
          <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
            Este produto possui uma configuração especial. Entre em contato
            para comprar.
          </p>
        ) : product.available ? (
          <>
            {productFamily ? <ProductFamilyOptions data={productFamily} /> : null}
            <div className="grid grid-cols-[minmax(96px,min(30%,140px))_minmax(0,1fr)] items-end gap-3">
              <ProductQuantity
                value={quantity}
                max={product.stockQuantity}
                onChange={setQuantity}
                compact
                fullWidthOnMobile={false}
              />
              <Button
                ref={mainPurchaseButtonRef}
                variant="primary"
                size="lg"
                className="h-[50px] min-w-0 flex-1 rounded-xl text-xl font-medium"
                onClick={() => void handlePurchase()}
                disabled={
                  isAdding ||
                  (isVariable &&
                    (!selectedVariation ||
                      !selectedVariation.purchasable ||
                      !selectedVariation.inStock))
                }
              >
                {isAdding ? "Adicionando..." : "Adicionar ao carrinho"}
              </Button>
            </div>
            <p
              className="mt-2 text-sm text-muted"
              role="status"
              aria-live="polite"
            >
              {purchaseMessage || variationSelectionMessage}
            </p>
          </>
        ) : (
          <BackInStockForm
            productId={product.id}
            productName={product.name}
            productUrl={getProductHref(product.slug)}
            integrationEnabled={stockNotificationEnabled}
            whatsappEnabled={stockNotificationWhatsapp}
            requiresVariation={product.variations.length > 0}
          />
        )}
      </div>

      {boughtTogetherItems.length > 0 ? (
        <FrequentlyBoughtTogether
          mainProduct={product}
          items={boughtTogetherItems}
        />
      ) : null}

      {product.available ? (
        <div className="mt-6">
          <ShippingCalculator
            mode="product"
            contextKey={shippingContextKey}
            product={
              isVariable && !selectedVariation
                ? undefined
                : shippingProduct
            }
            productInfo={{ name: product.name, url: `${SITE_URL}${getProductHref(product.slug)}` }}
            onSelectionChange={setSelectedShipping}
          />
        </div>
      ) : null}

      <ul className="mt-5 grid gap-3 text-sm text-foreground sm:grid-cols-3">
        <li className="flex items-center gap-2">
          <ShieldCheck
            className="h-5 w-5 shrink-0 text-secondary"
            aria-hidden="true"
          />
          Compra segura
        </li>
        <li className="flex items-center gap-2">
          <Store
            className="h-5 w-5 shrink-0 text-secondary"
            aria-hidden="true"
          />
          Retirada na loja
        </li>
        <li className="flex items-center gap-2">
          <Truck
            className="h-5 w-5 shrink-0 text-secondary"
            aria-hidden="true"
          />
          Entrega para Jundiaí e região
        </li>
      </ul>

      {product.available ? (
        <div
          aria-hidden={!showStickyPurchase}
          inert={!showStickyPurchase}
          className={`fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur transition-[transform,opacity,visibility] duration-300 ease-out ${
            showStickyPurchase
              ? "visible translate-y-0 opacity-100"
              : "invisible translate-y-full opacity-0"
          }`}
        >
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:gap-4">
            <div className="hidden min-w-0 flex-1 md:block">
              <p className="line-clamp-1 text-sm font-semibold text-foreground">
                {product.name}
              </p>
              <p className="mt-0.5 text-sm font-bold text-emerald-700">
                {currencyFormatter.format(payment.pixPrice)} no Pix
              </p>
            </div>
            <div className="grid min-w-0 flex-1 grid-cols-[minmax(96px,min(30%,140px))_minmax(0,1fr)] items-center gap-3 md:max-w-xl">
              <ProductQuantity
                value={quantity}
                max={product.stockQuantity}
                onChange={setQuantity}
                compact
                fullWidthOnMobile={false}
                showLabel={false}
              />
              <Button
                variant="primary"
                size="lg"
                className="h-[50px] min-w-0 rounded-xl px-4 text-base font-medium sm:text-lg"
                onClick={() => void handlePurchase()}
                disabled={
                  isAdding ||
                  (isVariable &&
                    (!selectedVariation ||
                      !selectedVariation.purchasable ||
                      !selectedVariation.inStock))
                }
              >
                {isAdding ? "Adicionando..." : "Adicionar ao carrinho"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
